/**
 * PÁGINA DE RESTABLECIMIENTO DE CONTRASEÑA
 * 
 * Recibe token del email y permite cambiar contraseña.
 * Después del cambio exitoso, hace handoff SSO a la app destino.
 */

import React, { useState, FormEvent, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getBrandingConfig } from '@shared/config/brandingConfig';
import { supabase } from '@shared/lib/supabaseClient';
import { Button } from '@shared/ui/shadcn/ui/button';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { Input } from '@shared/ui/shadcn/ui/input';
import { Eye, EyeOff } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';
import { createSsoTrace } from '@shared/utils/ssoTrace';
import { notifyCoreAPILogin } from '@shared/services/coreAuthService';
import { getDefaultReturnTo, resolveHandoffAppBaseUrl, APP_SSO_CONFIGS } from '@shared/navigation/appSSOConfig';
import { useDynamicBranding } from '../hooks/useDynamicBranding';

/**
 * 🔧 Helper: Obtener URL del Core API según el entorno
 * - DEV: localhost:4000 (Core API local)
 * - PROD: api.elfom.com (producción - SSO centralizado)
 */
const getCoreApiUrl = (): string => {
  // ✅ Limpiar URL de espacios y saltos de línea (problema común en variables de entorno)
  // Si está definida en variables de entorno, usarla
  if (import.meta.env.VITE_CORE_API_URL?.trim()) {
    return import.meta.env.VITE_CORE_API_URL.trim();
  }
  
  // Fallback según entorno
  return import.meta.env.DEV 
    ? 'http://localhost:4000' 
    : 'https://api.elfom.com';
};

/**
 * Normaliza returnTo: convierte URLs absolutas a rutas relativas
 */
const normalizeReturnTo = (returnToRaw: string | null, _app: string): string | null => {
  if (!returnToRaw) return null;

  try {
    if (returnToRaw.startsWith('http://') || returnToRaw.startsWith('https://')) {
      const u = new URL(returnToRaw);
      return (u.pathname || '/') + (u.search || '');
    }
  } catch {}

  if (returnToRaw.startsWith('/') && !returnToRaw.startsWith('//')) return returnToRaw;

  return null;
};

/**
 * HANDOFF SSO: Crear handoff y redirigir a app destino
 * Reutiliza la lógica de Login.tsx
 */
const handleHandoffRedirect = async (
  app: string,
  returnTo: string | null,
  onError: (message: string) => void,
  trace: ReturnType<typeof createSsoTrace>,
  clientOriginHint?: string | null,
): Promise<boolean> => {
  try {
    trace.step('SESSION_GET_START');
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) {
      trace.error('SESSION_GET_ERROR', { error: sessionError?.message });
      onError('Error: No se pudo obtener la sesión. Por favor intenta de nuevo.');
      return false;
    }

    if (!session.access_token) {
      trace.error('SESSION_INVALID', { reason: 'missing_access_token' });
      onError('Error: Sesión inválida. Por favor intenta de nuevo.');
      return false;
    }

    trace.step('SESSION_GET_OK', {
      hasAccessToken: !!session.access_token,
      hasRefreshToken: !!session.refresh_token,
      accessTokenMasked: trace.maskToken(session.access_token),
      userId: session.user?.id,
    });

    let normalizedApp = app?.trim() || null;
    
    if (!normalizedApp || !APP_SSO_CONFIGS[normalizedApp]) {
      const supportedApps = Object.keys(APP_SSO_CONFIGS).join(', ');
      trace.error('APP_NOT_SUPPORTED', { 
        app: normalizedApp || 'null',
        supportedApps,
      });
      onError(`Error: La aplicación "${normalizedApp || 'desconocida'}" no está soportada. Apps soportadas: ${supportedApps}`);
      return false;
    }
    
    const normalizedReturnTo = normalizeReturnTo(returnTo, normalizedApp);
    const defaultRoute = getDefaultReturnTo(normalizedApp);
    const safeReturnTo = (normalizedReturnTo && normalizedReturnTo !== '/') 
      ? normalizedReturnTo 
      : (defaultRoute || '/dashboard');
    
    const appBaseUrlResolved = resolveHandoffAppBaseUrl(normalizedApp, clientOriginHint);

    if (!appBaseUrlResolved) {
      trace.error('APP_ORIGIN_NOT_FOUND', { app: normalizedApp });
      onError(`Error: No se pudo determinar la URL de destino para la aplicación "${normalizedApp}"`);
      return false;
    }

    const coreApiUrl = getCoreApiUrl();
    
    const handoffBody: any = {
      app: normalizedApp,
    };

    if (session.refresh_token) {
      handoffBody.session = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      };
    }

    if (safeReturnTo && safeReturnTo.startsWith('/')) {
      handoffBody.returnTo = safeReturnTo;
    }

    const handoffUrl = `${coreApiUrl}/auth/handoff`;
    trace.step('HANDOFF_POST_START', {
      url: handoffUrl,
      app: normalizedApp,
      returnTo: safeReturnTo,
      traceId: trace.traceId,
    });

    const handoffResponse = await fetch(handoffUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'X-Trace-Id': trace.traceId,
      },
      body: JSON.stringify(handoffBody),
    });

    trace.step('HANDOFF_POST_RESPONSE', {
      status: handoffResponse.status,
      ok: handoffResponse.ok,
    });

    if (!handoffResponse.ok) {
      const errorData = await handoffResponse.json().catch(() => ({ error: 'Error desconocido' }));
      trace.error('HANDOFF_POST_ERROR', {
        status: handoffResponse.status,
        error: errorData.error || 'Unknown error',
      });
      onError(`Error: ${errorData.message || errorData.error || 'Error al crear código de acceso'}. Por favor intenta de nuevo.`);
      return false;
    }

    const handoffData = await handoffResponse.json();
    const { code } = handoffData;

    if (!code) {
      trace.error('HANDOFF_RESPONSE_NO_CODE', { response: handoffData });
      onError('Error: No se recibió código de acceso. Por favor intenta de nuevo.');
      return false;
    }

    const returnToPath = safeReturnTo;
    const redirectUrl = `${appBaseUrlResolved}/auth/callback?code=${encodeURIComponent(code)}&returnTo=${encodeURIComponent(returnToPath)}&traceId=${encodeURIComponent(trace.traceId)}`;

    trace.step('REDIRECT_EXECUTE', {
      finalUrl: redirectUrl.replace(code, trace.maskCode(code)),
    });

    window.location.href = redirectUrl;
    return true;
  } catch (err: any) {
    trace.error('HANDOFF_UNEXPECTED_ERROR', {
      error: err.message,
    });
    onError('Error inesperado. Por favor intenta de nuevo.');
    return false;
  }
};

const ResetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const app = params.get('app') ?? 'default';
  const returnTo = params.get('returnTo');
  const clientOriginParam = params.get('clientOrigin');
  // ✅ Aplicar branding dinámico (fallback a 'default' si app es desconocido)
  useDynamicBranding(app);
  const branding = getBrandingConfig(app);
  const trace = createSsoTrace({ app, feature: 'SSO_HANDOFF' });

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  
  // Estados de foco para placeholders condicionales
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);

  // Verificar si hay token válido en la URL (Supabase lo maneja automáticamente)
  useEffect(() => {
    const checkSession = async () => {
      try {
        // Supabase automáticamente valida el token cuando se accede a la URL
        // Si el token es válido, habrá una sesión temporal
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          setIsTokenValid(true);
          trace.step('TOKEN_VALID', { hasSession: true });
        } else {
          // Verificar si hay hash params (Supabase usa hash para tokens)
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const type = hashParams.get('type');
          
          if (accessToken && type === 'recovery') {
            // Establecer sesión temporal con el token
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: hashParams.get('refresh_token') || '',
            });
            
            if (!sessionError) {
              setIsTokenValid(true);
              trace.step('TOKEN_VALID_FROM_HASH', {});
            } else {
              setIsTokenValid(false);
              trace.error('TOKEN_INVALID', { error: sessionError.message });
            }
          } else {
            setIsTokenValid(false);
            trace.error('NO_TOKEN_FOUND', {});
          }
        }
      } catch (err: any) {
        setIsTokenValid(false);
        trace.error('TOKEN_CHECK_ERROR', { error: err.message });
      }
    };

    checkSession();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validaciones
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      trace.step('UPDATE_PASSWORD_START', {});

      // Actualizar contraseña (Supabase requiere sesión activa)
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        trace.error('UPDATE_PASSWORD_ERROR', { error: updateError.message });
        setError(updateError.message || 'Error al actualizar contraseña');
        setLoading(false);
        return;
      }

      trace.step('UPDATE_PASSWORD_SUCCESS', {});

      // Notificar al Core API del cambio de contraseña (mantener sesión)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        await notifyCoreAPILogin(session.access_token);
      }

      setSuccess(true);
      setLoading(false);

      // Esperar un momento y luego hacer handoff SSO
      setTimeout(async () => {
        const handoffSuccess = await handleHandoffRedirect(
          app,
          returnTo,
          (errorMessage) => {
            setError(errorMessage);
            setSuccess(false);
          },
          trace,
          clientOriginParam,
        );

        if (!handoffSuccess) {
          // Si handoff falla, redirigir a login
          navigate(
            `/login?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo || '')}` : ''}${
              clientOriginParam ? `&clientOrigin=${encodeURIComponent(clientOriginParam)}` : ''
            }`,
          );
        }
      }, 1500);
    } catch (err: any) {
      trace.error('UPDATE_PASSWORD_EXCEPTION', { error: err.message });
      setError('Error inesperado. Por favor intenta de nuevo.');
      setLoading(false);
    }
  };

  // Mostrar loading mientras se verifica el token
  if (isTokenValid === null) {
    return (
      <AuthLayout>
        <div className="w-full flex flex-col gap-6">
          <div className="text-center space-y-3">
            {app === 'houseme' ? (
              <div className="flex items-center justify-center gap-2">
                <img src={branding.assets.logo || "/icon-houseme-192x192.png"} alt="HouseMe" className="h-8 w-8" />
                <span className="text-lg lg:text-xl font-bold">
                  <span style={{ color: `hsl(${branding.colors.primary})` }}>House</span>
                  <span style={{ color: `hsl(${branding.colors.secondary || branding.colors.primary})` }}>Me</span>
                </span>
              </div>
            ) : app === 'empresa' ? (
              <div className="flex items-center justify-center gap-2">
                <img 
                  src={branding.assets.logo || "/icon-192x192.png"} 
                  alt="ThePlatform360" 
                  className="h-8 w-8"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (target.src !== window.location.origin + '/icon-192x192.png') {
                      target.src = '/icon-192x192.png';
                    }
                  }}
                />
                <span className="text-lg lg:text-xl font-bold" style={{ color: `hsl(${branding.colors.primary})` }}>
                  ThePlatform360
                </span>
              </div>
            ) : branding.assets.logo ? (
              <img src={branding.assets.logo} alt={branding.label} className="h-12 mx-auto" />
            ) : null}
            <h1 className="text-xl font-semibold text-foreground">Verificando...</h1>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Token inválido o expirado
  if (isTokenValid === false) {
    return (
      <AuthLayout>
        <div className="w-full flex flex-col gap-6">
          <div className="text-center space-y-3">
            {app === 'houseme' ? (
              <div className="flex items-center justify-center gap-2">
                <img src={branding.assets.logo || "/icon-houseme-192x192.png"} alt="HouseMe" className="h-8 w-8" />
                <span className="text-lg lg:text-xl font-bold">
                  <span style={{ color: `hsl(${branding.colors.primary})` }}>House</span>
                  <span style={{ color: `hsl(${branding.colors.secondary || branding.colors.primary})` }}>Me</span>
                </span>
              </div>
            ) : app === 'empresa' ? (
              <div className="flex items-center justify-center gap-2">
                <img 
                  src={branding.assets.logo || "/icon-192x192.png"} 
                  alt="ThePlatform360" 
                  className="h-8 w-8"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (target.src !== window.location.origin + '/icon-192x192.png') {
                      target.src = '/icon-192x192.png';
                    }
                  }}
                />
                <span className="text-lg lg:text-xl font-bold" style={{ color: `hsl(${branding.colors.primary})` }}>
                  ThePlatform360
                </span>
              </div>
            ) : branding.assets.logo ? (
              <img src={branding.assets.logo} alt={branding.label} className="h-12 mx-auto" />
            ) : null}
            
            <div>
              <h1 className="text-xl font-semibold text-foreground">Enlace expirado</h1>
              <p className="text-sm text-muted-foreground mt-1">
                El enlace de recuperación ha expirado o es inválido
              </p>
            </div>
          </div>

          <Alert variant="destructive">
            <AlertDescription>
              Por favor solicita un nuevo enlace de recuperación
            </AlertDescription>
          </Alert>

          <Button
            onClick={() => navigate(`/forgot-password?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`)}
            className="w-full"
            size="lg"
          >
            Solicitar nuevo enlace
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // Token válido - mostrar formulario
  return (
    <AuthLayout>
      {/* ✅ DISEÑO MODERNO - Consistente con Login */}
      <div className="w-full flex flex-col gap-6">
        {/* Logo + Título */}
        <div className="text-center space-y-3">
          {app === 'houseme' ? (
            <div className="flex items-center justify-center gap-2">
              <img src={branding.assets.logo || "/icon-houseme-192x192.png"} alt="HouseMe" className="h-8 w-8" />
              <span className="text-lg lg:text-xl font-bold">
                <span style={{ color: `hsl(${branding.colors.primary})` }}>House</span>
                <span style={{ color: `hsl(${branding.colors.secondary || branding.colors.primary})` }}>Me</span>
              </span>
            </div>
          ) : app === 'empresa' ? (
            <div className="flex items-center justify-center gap-2">
              <img 
                src={branding.assets.logo || "/icon-192x192.png"} 
                alt="ThePlatform360" 
                className="h-8 w-8"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (target.src !== window.location.origin + '/icon-192x192.png') {
                    target.src = '/icon-192x192.png';
                  }
                }}
              />
              <span className="text-lg lg:text-xl font-bold" style={{ color: `hsl(${branding.colors.primary})` }}>
                ThePlatform360
              </span>
            </div>
          ) : branding.assets.logo ? (
            <img src={branding.assets.logo} alt={branding.label} className="h-12 mx-auto" />
          ) : null}
          
          <div>
            <h1 className="text-xl font-semibold text-foreground">Nueva contraseña</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Ingresa tu nueva contraseña
            </p>
          </div>
        </div>

        {success ? (
          <div className="text-center space-y-4">
            <Alert>
              <AlertDescription className="text-center">
                ✅ Contraseña actualizada exitosamente. Redirigiendo...
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    label="Nueva Contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    required
                    autoComplete="new-password"
                    placeholder={passwordFocused ? "Escríbela aquí" : ""}
                    className="pr-10 placeholder:text-muted-foreground/40"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirmar Contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setConfirmPasswordFocused(true)}
                    onBlur={() => setConfirmPasswordFocused(false)}
                    required
                    autoComplete="new-password"
                    placeholder={confirmPasswordFocused ? "Escríbela aquí" : ""}
                    className="pr-10 placeholder:text-muted-foreground/40"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full" size="lg">
                {loading ? 'Actualizando...' : 'Actualizar contraseña'}
              </Button>
            </form>

            {/* Footer compacto */}
            <div className="mt-4 text-center text-xs text-muted-foreground">
              <button
                type="button"
                onClick={() => navigate(`/login?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`)}
                className="text-primary hover:underline font-medium"
              >
                Volver al login
              </button>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
